import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LogOut, ShieldAlert, ShieldCheck, Trash2, CheckCircle } from 'lucide-react';
import PlayerCard from '@/components/PlayerCard';
import AddPlayerModal from '@/components/AddPlayerModal';
import DragTutorial from '@/components/DragTutorial';
import { FORMATIONS } from '@/utils/formations';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_REFRESH_INTERVAL = 10000; // 10 seconds

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [formation, setFormation] = useState('4-3-3');
  const [players, setPlayers] = useState([]);
  const [teamStatus, setTeamStatus] = useState('draft');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveTimeout, setSaveTimeout] = useState(null);
  const [draggedSlot, setDraggedSlot] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [hasScrolledToPitch, setHasScrolledToPitch] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadUserAndTeam();
    
    // Set up auto-refresh interval to check for ban status changes
    const intervalId = setInterval(() => {
      refreshTeamStatusSilent();
    }, AUTO_REFRESH_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, []);

  // Silent refresh to check if user was unbanned or team status changed
  const refreshTeamStatusSilent = async () => {
    try {
      const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (!userRes.ok) return;
      
      const userData = await userRes.json();
      
      // If user was banned, redirect to login
      if (userData.is_banned) {
        const banReason = encodeURIComponent(userData.ban_reason || 'Não especificado');
        const email = encodeURIComponent(userData.email);
        navigate(`/login?banned=true&reason=${banReason}&email=${email}`);
        return;
      }

      // Refresh team data
      const teamRes = await fetch(`${BACKEND_URL}/api/teams/my-team`, {
        credentials: 'include'
      });
      
      if (teamRes.ok) {
        const teamData = await teamRes.json();
        if (teamData) {
          // Only update if there are actual changes to avoid UI flicker
          if (teamData.status !== teamStatus) {
            setTeamStatus(teamData.status || 'draft');
          }
        }
      }
    } catch (error) {
      console.error('Silent refresh error:', error);
    }
  };

  // Check if tutorial should be shown
  useEffect(() => {
    const tutorialSeen = localStorage.getItem('copa_coruja_tutorial_seen');
    if (!tutorialSeen && !loading) {
      // Show tutorial after a short delay
      const timer = setTimeout(() => {
        // Check if user has scrolled to pitch
        const handleScroll = () => {
          const pitchElement = document.querySelector('[data-testid="pitch-view"]');
          if (pitchElement) {
            const rect = pitchElement.getBoundingClientRect();
            if (rect.top < window.innerHeight && !hasScrolledToPitch) {
              setHasScrolledToPitch(true);
              setShowTutorial(true);
              window.removeEventListener('scroll', handleScroll);
            }
          }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Check immediately in case already scrolled

        return () => window.removeEventListener('scroll', handleScroll);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [loading, hasScrolledToPitch]);

  const handleCloseTutorial = () => {
    setShowTutorial(false);
    localStorage.setItem('copa_coruja_tutorial_seen', 'true');
  };

  // Auto-save team name with debounce
  useEffect(() => {
    if (!teamName || loading) return;
    
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    const timeout = setTimeout(() => {
      saveTeamToServer();
    }, 1000); // Save 1 second after user stops typing

    setSaveTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [teamName]);

  // Auto-save when formation changes
  useEffect(() => {
    if (!loading && formation) {
      saveTeamToServer();
    }
  }, [formation]);

  const loadUserAndTeam = async () => {
    try {
      const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (!userRes.ok) {
        navigate('/login');
        return;
      }
      
      const userData = await userRes.json();
      
      // Check if user is banned
      if (userData.is_banned) {
        const banReason = encodeURIComponent(userData.ban_reason || 'Não especificado');
        const email = encodeURIComponent(userData.email);
        navigate(`/login?banned=true&reason=${banReason}&email=${email}`);
        return;
      }
      
      setUser(userData);

      const teamRes = await fetch(`${BACKEND_URL}/api/teams/my-team`, {
        credentials: 'include'
      });
      
      // Check if banned when accessing team
      if (teamRes.status === 403) {
        const errorData = await teamRes.json();
        toast.error(errorData.detail || 'Acesso negado', { duration: 10000 });
        navigate('/login');
        return;
      }
      
      if (teamRes.ok) {
        const teamData = await teamRes.json();
        if (teamData) {
          setTeamName(teamData.team_name);
          setFormation(teamData.formation);
          setPlayers(teamData.players);
          setTeamStatus(teamData.status || 'draft');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTeamToServer = async () => {
    if (!teamName.trim() || isSaving) return;

    try {
      setIsSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          team_name: teamName,
          formation: formation,
          players: players
        })
      });

      if (!response.ok) throw new Error('Failed to save team');
      
      const savedTeam = await response.json();
      // Don't overwrite players here - it can cause race conditions with handleSavePlayer
      // The players state is already correct from optimistic updates
      if (savedTeam.status) {
        setTeamStatus(savedTeam.status);
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erro ao salvar time');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSlotClick = (slotNumber) => {
    setSelectedSlot(slotNumber);
    setShowModal(true);
  };

  const handleSavePlayer = async (playerData) => {
    const newPlayer = { ...playerData, slot_number: selectedSlot };
    const currentSlot = selectedSlot; // Capture before clearing
    
    // Calculate updated players array
    const updatedPlayers = [...players];
    const existingIndex = updatedPlayers.findIndex(p => p.slot_number === currentSlot);
    
    if (existingIndex >= 0) {
      updatedPlayers[existingIndex] = newPlayer;
    } else {
      updatedPlayers.push(newPlayer);
    }
    
    // Update state immediately (optimistic update)
    setPlayers(updatedPlayers);
    setShowModal(false);
    setSelectedSlot(null);
    
    // Save player change to backend
    try {
      setIsSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          team_name: teamName,
          formation: formation,
          players: updatedPlayers
        })
      });

      if (!response.ok) throw new Error('Failed to save team');
      
      const savedTeam = await response.json();
      
      // Only update status from server, NOT players (we already have the correct state from optimistic update)
      if (savedTeam.status) {
        setTeamStatus(savedTeam.status);
      }
      
      toast.success('Jogador salvo!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erro ao salvar jogador');
      // Revert to previous state on error
      loadUserAndTeam();
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidateAndSubmit = async () => {
    // Validar nome do time primeiro
    if (!teamName || !teamName.trim()) {
      toast.error('Preencha o nome do time para cadastrar!', {
        duration: 5000
      });
      return;
    }

    if (!isValid) {
      toast.error('Time ainda não pode ser cadastrado! Está como rascunho. Preencha os requisitos para cadastrar.', {
        duration: 5000
      });
      return;
    }

    // Se o time está pronto para cadastro, registra
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams/submit`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao cadastrar time');
      }
      
      setTeamStatus('registered');
      toast.success('🎉 Time cadastrado com sucesso!', {
        duration: 5000
      });
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Erro ao cadastrar time');
    }
  };

  const handleResetTeam = async () => {
    try {
      // Get current team to delete
      const teamRes = await fetch(`${BACKEND_URL}/api/teams/my-team`, {
        credentials: 'include'
      });
      
      if (teamRes.ok) {
        const teamData = await teamRes.json();
        if (teamData && teamData.team_id) {
          // Delete team from backend
          await fetch(`${BACKEND_URL}/api/teams/${teamData.team_id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
        }
      }
      
      // Reset local state
      setTeamName('');
      setFormation('4-3-3');
      setPlayers([]);
      setShowResetDialog(false);
      
      toast.success('Time resetado com sucesso!');
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Erro ao resetar time');
    }
  };

  const getPlayerBySlot = (slotNumber) => {
    return players.find(p => p.slot_number === slotNumber);
  };

  const handleDragStart = (slotNumber) => {
    const player = getPlayerBySlot(slotNumber);
    if (!player) return; // Only allow dragging if there's a player
    setDraggedSlot(slotNumber);
  };

  const handleDragOver = (e, slotNumber) => {
    e.preventDefault();
    setDragOverSlot(slotNumber);
  };

  const handleDragEnd = () => {
    setDraggedSlot(null);
    setDragOverSlot(null);
  };

  const handleDrop = async (e, targetSlot) => {
    e.preventDefault();
    
    if (!draggedSlot || draggedSlot === targetSlot || isSaving) {
      handleDragEnd();
      return;
    }

    const updatedPlayers = [...players];
    const draggedPlayer = getPlayerBySlot(draggedSlot);
    const targetPlayer = getPlayerBySlot(targetSlot);

    if (!draggedPlayer) {
      handleDragEnd();
      return;
    }

    if (targetPlayer) {
      // Swap positions
      const draggedIndex = updatedPlayers.findIndex(p => p.slot_number === draggedSlot);
      const targetIndex = updatedPlayers.findIndex(p => p.slot_number === targetSlot);
      
      updatedPlayers[draggedIndex] = { ...draggedPlayer, slot_number: targetSlot };
      updatedPlayers[targetIndex] = { ...targetPlayer, slot_number: draggedSlot };
      
      toast.success('Jogadores trocados de posição!');
    } else {
      // Move to empty slot
      const draggedIndex = updatedPlayers.findIndex(p => p.slot_number === draggedSlot);
      updatedPlayers[draggedIndex] = { ...draggedPlayer, slot_number: targetSlot };
      
      toast.success('Jogador movido!');
    }

    setPlayers(updatedPlayers);
    handleDragEnd();

    // Auto-save with lock - don't update players state again to avoid flicker
    try {
      setIsSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          team_name: teamName,
          formation: formation,
          players: updatedPlayers
        })
      });
      
      if (response.ok) {
        const savedTeam = await response.json();
        // Only update status, NOT players (optimistic update already correct)
        if (savedTeam.status) {
          setTeamStatus(savedTeam.status);
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erro ao salvar');
    } finally {
      setIsSaving(false);
    }
  };

  const totalPlayersCount = players.length;
  const realPlayersCount = players.filter(p => p.type === 'real').length;
  const isValid = totalPlayersCount === 11 && realPlayersCount >= 7;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#090A0F]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#FF5A00] border-t-transparent mb-4" />
          <p className="text-white text-lg">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1676746424139-77f8bd8922a8)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="dashboard-page"
    >
      <div className="noise-overlay" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#090A0F] via-black/90 to-[#090A0F]" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="glass-card mx-4 mt-4 p-4 flex items-center justify-between relative z-40">
          <div className="flex items-center gap-4">
            <img 
              src={user?.picture || 'https://via.placeholder.com/48'} 
              alt={user?.name}
              className="w-12 h-12 rounded-full border-2 border-[#FF5A00]"
            />
            <div>
              <h2 className="text-white font-semibold" style={{ fontFamily: 'Outfit' }}>{user?.name}</h2>
              <p className="text-[#A1A1AA] text-sm">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <Button
                onClick={() => navigate('/admin')}
                className="btn-secondary"
                data-testid="admin-panel-button"
              >
                Painel Admin
              </Button>
            )}
            <Button
              onClick={handleLogout}
              className="btn-secondary"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mt-4 sm:mt-6 md:mt-8 mb-4 sm:mb-6 px-4 relative z-30">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
            Copa <span className="text-[#FF5A00]">Coruja</span>
          </h1>
          <p className="text-[#A1A1AA] text-base sm:text-lg">Cadastre seu time para a Copa Coruja</p>
        </div>

        {/* Team Config */}
        <div className="w-full px-2 sm:px-4 mb-4 sm:mb-6 relative z-30">
          <div className="glass-card p-4 sm:p-5 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div>
                <label className="text-white font-semibold mb-2 block text-sm sm:text-base">Nome do Time</label>
                <Input
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Digite o nome do seu time"
                  className="bg-white/5 border-white/10 text-white text-sm sm:text-base"
                  data-testid="team-name-input"
                />
              </div>
              <div>
                <label className="text-white font-semibold mb-2 block text-sm sm:text-base">Formação</label>
                <Select value={formation} onValueChange={setFormation}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm sm:text-base" data-testid="formation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14151C] border-white/10">
                    {Object.keys(FORMATIONS).map(key => (
                      <SelectItem key={key} value={key} className="text-white hover:bg-white/10 text-sm sm:text-base">
                        {key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-4 sm:mt-6">
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                {/* Status Badge */}
                {teamStatus === 'registered' && (
                  <div className="flex items-center gap-2 bg-green-500/10 text-[#00FF66] px-3 py-2 rounded-lg mb-2">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-semibold">Time Cadastrado com Sucesso!</span>
                  </div>
                )}
                
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-semibold text-sm sm:text-base">
                    Jogadores Cadastrados: <span className={isValid ? 'text-[#00FF66]' : 'text-[#FF5A00]'}>{totalPlayersCount}/11</span>
                  </span>
                  {teamStatus === 'draft' && (
                    isValid ? (
                      <span className="text-[#00FF66] text-xs sm:text-sm font-semibold flex items-center gap-1">
                        <span className="text-[#00FF66]">✓</span> Pronto para Cadastrar
                      </span>
                    ) : (
                      <span 
                        className="text-[#FF5A00] text-xs sm:text-sm font-semibold cursor-help"
                        title="Preencha todas as 11 posições com pelo menos 7 jogadores reais para cadastrar o time"
                      >
                        Time em Rascunho
                      </span>
                    )
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {realPlayersCount >= 7 ? (
                    <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5 text-[#00FF66]" />
                  ) : (
                    <ShieldAlert className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
                  )}
                  <span 
                    className="text-white font-semibold cursor-help text-sm sm:text-base"
                    title="Para cadastrar o time, adicione pelo menos 7 jogadores reais"
                  >
                    Jogadores Reais: <span className={realPlayersCount >= 7 ? 'text-[#00FF66]' : 'text-yellow-500'}>{realPlayersCount}/7</span>
                  </span>
                  <span 
                    className="text-[#A1A1AA] text-xs sm:text-sm cursor-help"
                    title="Para cadastrar o time, adicione pelo menos 7 jogadores reais"
                  >
                    (mínimo 7 jogadores)
                  </span>
                </div>
              </div>
              {teamStatus === 'draft' && (
                <Button
                  onClick={handleValidateAndSubmit}
                  className={`${isValid ? 'btn-primary' : 'btn-secondary'} w-full sm:w-auto text-sm sm:text-base whitespace-nowrap`}
                  data-testid="validate-submit-button"
                >
                  CADASTRAR TIME
                </Button>
              )}
              <Button
                onClick={() => setShowResetDialog(true)}
                variant="destructive"
                className="w-full sm:w-auto text-sm sm:text-base bg-red-600 hover:bg-red-700"
                data-testid="reset-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                RESETAR TIME
              </Button>
            </div>
          </div>
        </div>

        {/* Pitch View - RESPONSIVO E CENTRALIZADO */}
        <div 
          className="max-w-6xl mx-auto px-2 sm:px-4 pb-4 sm:pb-8 relative z-10"
          data-testid="pitch-view"
        >
          <div
            className="mx-auto relative"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1599832904773-54aa7d6e81e1)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '12px',
              minHeight: '850px',
              maxWidth: '100%',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            className="sm:min-h-[950px] md:min-h-[1000px] lg:min-h-[1100px]"
          >
            <div className="absolute inset-0 bg-black/50 rounded-2xl" />
            <div className="relative z-10 p-6 sm:p-8 md:p-12 lg:p-16">
              <div 
                className="relative"
                style={{ minHeight: '780px' }}
                className="sm:min-h-[850px] md:min-h-[900px] lg:min-h-[1000px]"
              >
                {FORMATIONS[formation].map((position, index) => {
                  const slotNumber = index + 1;
                  const player = getPlayerBySlot(slotNumber);
                  const isDragging = draggedSlot === slotNumber;
                  const isDragOver = dragOverSlot === slotNumber;
                  
                  // Key includes player data to force re-render when player changes
                  const playerKey = player 
                    ? `${slotNumber}-${player.type}-${player.game_tag || 'bot'}-${player.photo_url || ''}`
                    : `${slotNumber}-empty`;
                  
                  return (
                    <PlayerCard
                      key={playerKey}
                      slotNumber={slotNumber}
                      position={position}
                      player={player}
                      onClick={() => handleSlotClick(slotNumber)}
                      onDragStart={() => handleDragStart(slotNumber)}
                      onDragOver={(e) => handleDragOver(e, slotNumber)}
                      onDrop={(e) => handleDrop(e, slotNumber)}
                      onDragEnd={handleDragEnd}
                      isDragging={isDragging}
                      isDragOver={isDragOver}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <AddPlayerModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setSelectedSlot(null); }}
          onSave={handleSavePlayer}
          initialData={getPlayerBySlot(selectedSlot)}
        />
      )}

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent className="glass-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white text-xl">
              Tem certeza que deseja resetar o time?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Todas as informações do seu time serão deletadas permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetTeam}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Sim, Resetar Time
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Drag Tutorial */}
      {showTutorial && <DragTutorial onClose={handleCloseTutorial} />}
    </div>
  );
}